import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

export default function EventAttendees() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const [eventRes, attendeesRes] = await Promise.all([
        api.get(`/hr/events/${eventId}`).catch(() => ({ data: {} })),
        api.get(`/hr/events/${eventId}/attendees`),
      ]);
      setEvent(eventRes.data?.data || eventRes.data);
      setAttendees(attendeesRes.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch attendees');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString();
    } catch {
      return d;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Event Attendees</Box>
          </>
        }
        title={event?.name ? `Attendees — ${event.name}` : 'Event Attendees'}
        actions={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/events')}
            variant="outlined"
            sx={{ borderColor: tokens.border, color: tokens.ink }}
          >
            Back to Events
          </Button>
        }
      />
      <Paper elevation={0} sx={{ ...surfaceCardSx, p: 2 }}>
        {event?.date && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {formatDate(event.date)} {event.location && `• ${event.location}`}
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Member ID</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Attended</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attendees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No records yet
                  </TableCell>
                </TableRow>
              ) : (
                attendees.map((a) => (
                  <TableRow key={a.member_id || a.id}>
                    <TableCell>{[a.first_name, a.last_name].filter(Boolean).join(' ')}</TableCell>
                    <TableCell>{a.member_id || '-'}</TableCell>
                    <TableCell>{a.email || '-'}</TableCell>
                    <TableCell>{a.phone || '-'}</TableCell>
                    <TableCell>{a.attended ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
